import { PepDetailsModel } from '@marketplace-web/pay-identity/politically-exposed-person-data'
import { AddressModel } from '@marketplace-web/pay-identity/payments-address-data'

import {
  SubmitPaymentsIdentityArgs,
  PaymentsIdentityFileModel,
  BusinessRegistrationRepresentativeDto,
  BusinessAccountAddressDto,
} from '../types/payments-identity'

const addressToParams = (address: AddressModel | null) =>
  (address && {
    country_id: address.countryId,
    name: address.name || null,
    city: address.city || null,
    line1: address.line1 || null,
    line2: address.line2 || null,
    postal_code: address.postalCode || null,
  }) ||
  null

const documentFileToParams = (file: PaymentsIdentityFileModel) => ({
  type: file.type,
  files: file.files.map(({ identifier, body, mediaType }) => ({
    identifier,
    body,
    media_type: mediaType,
  })),
})

export const pepDetailsToParams = (pepDetails: PepDetailsModel | null | undefined) => {
  if (!pepDetails) return undefined

  const pepParams: Record<string, unknown> = {}

  if (pepDetails.type) pepParams.type = pepDetails.type
  if (pepDetails.organisation) pepParams.organisation = pepDetails.organisation
  if (pepDetails.position) pepParams.position = pepDetails.position
  if (pepDetails.holdsPositionFrom) pepParams.holds_position_from = pepDetails.holdsPositionFrom
  if (pepDetails.associatedPepFullName)
    pepParams.associated_pep_full_name = pepDetails.associatedPepFullName

  return Object.keys(pepParams).length ? pepParams : undefined
}

export const submitPaymentsIdentityArgsToParams = ({
  firstName,
  lastName,
  birthdate,
  nationality,
  ssnSerial,
  personalIdNumber,
  address,
  ubos,
  documents,
  pepDetails,
}: SubmitPaymentsIdentityArgs) => {
  const transformedDocuments: Array<{
    type: string
    files: Array<{
      identifier: string
      uuid?: string | null
      mediaType?: string | null
      body?: string | null
    }>
  }> = documents?.map(document => documentFileToParams(document)) || []

  return {
    first_name: firstName,
    last_name: lastName,
    nationality,
    birthdate,
    ssn_serial: ssnSerial,
    personal_id_number: personalIdNumber,
    address: addressToParams(address),
    ubos:
      ubos?.map(ubo => ({
        first_name: ubo.firstName,
        last_name: ubo.lastName,
        birthday: ubo.birthday,
        nationality: ubo.nationality,
        birthplace_city: ubo.birthplaceCity,
        birthplace_country: ubo.birthplaceCountry,
        address: addressToParams(ubo.address),
      })) || null,
    documents: transformedDocuments,
    pep_details: pepDetailsToParams(pepDetails),
  }
}

export const transformPaymentsIdentityUbo = (
  kybData: SubmitPaymentsIdentityArgs,
  business_representative: BusinessRegistrationRepresentativeDto,
) => {
  if (!kybData.ubos) {
    return []
  }

  return [
    {
      ...kybData.ubos[0]!,
      firstName: business_representative.first_names ?? '',
      lastName: business_representative.last_name ?? '',
      birthday: business_representative.birthday,
      address: {
        ...business_representative.legal_representative_address,
        postalCode: business_representative.legal_representative_address?.postal_code,
        countryId: business_representative.legal_representative_address?.country_id,
        name: business_representative.full_name,
      },
    },
  ]
}

export const transformKybData = (
  kybData: SubmitPaymentsIdentityArgs,
  business_representative: BusinessRegistrationRepresentativeDto,
  business_address: BusinessAccountAddressDto,
) => {
  return {
    ...kybData,
    firstName: business_representative.first_names ?? '',
    lastName: business_representative.last_name ?? '',
    birthdate: business_representative.birthday,
    address: {
      city: business_address.city,
      countryId: business_address.country_id,
      line1: business_address.line1,
      line2: business_address.line2,
      name: business_address.name,
      postalCode: business_address.postal_code,
    },
  }
}
